/**
 * The heads-up display.
 *
 * A DOM overlay rather than canvas text: real font rendering, real
 * accessibility, and it costs the renderer nothing. It reads game snapshots and
 * never writes game state.
 *
 * The four things a player checks mid-play, in the order they check them:
 * how much am I on (score), how long have I got (timer), how close to death am
 * I (bust pips), and how much is riding on this play (streak). Everything else
 * is one step quieter.
 *
 * This file also holds the handful of DOM/motion helpers the screens share, so
 * the whole overlay stays inside four files.
 */

import { gsap } from 'gsap';
import { levelProgress } from '@/game/levels';
import { motion } from '@/core/tokens';
import { MAX_BUSTS, ROUND_SECONDS, STREAK_LABELS, type GameSnapshot } from '@/game/types';
import { streakBonusOf } from '@/game/scoring';
import { ladderColourAt, momentumAt } from '@/vfx/streakLadder';
import { GO_FLASH_MS } from '@/ui/go-flash';

/* ------------------------------------------------------------- helpers -- */

/** Terse element factory — `el('div', 'w21-hud-score__value', '0')`. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Tiny inline icon in `currentColor`. Drawn rather than typed, so no glyph
 * turns into a colour emoji on someone's phone.
 */
export function icon(kind: 'undo' | 'pause'): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', 'w21-btn__glyph');

  if (kind === 'undo') {
    const arc = document.createElementNS(SVG_NS, 'path');
    arc.setAttribute('d', 'M4 8h8.2a4.2 4.2 0 1 1 0 8.4H8.6');
    const head = document.createElementNS(SVG_NS, 'path');
    head.setAttribute('d', 'M7.2 4.6 3.8 8l3.4 3.4');
    for (const path of [arc, head]) {
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.7');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.append(path);
    }
    return svg;
  }

  for (const x of [6.2, 11]) {
    const bar = document.createElementNS(SVG_NS, 'rect');
    bar.setAttribute('x', String(x));
    bar.setAttribute('y', '4.6');
    bar.setAttribute('width', '2.8');
    bar.setAttribute('height', '10.8');
    bar.setAttribute('rx', '1.2');
    bar.setAttribute('fill', 'currentColor');
    svg.append(bar);
  }
  return svg;
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');

/** `12450` → `12,450`. Used for every number the player reads as money. */
export function formatScore(value: number): string {
  return NUMBER_FORMAT.format(Math.round(value));
}

/**
 * Resting scale of a spent bust pip.
 *
 * Lives here rather than in CSS because GSAP writes an inline transform when it
 * animates a pip and leaves it there, so a CSS `transform` on the spent state
 * would work exactly once and then be permanently overwritten.
 */
const PIP_SPENT_SCALE = 0.82;

/* ------------------------------------------------------------ the pill -- */

/**
 * The streak pill's escalation, in one place.
 *
 * The pill used to be the only element in the streak system that did not
 * escalate: it rendered in fixed `--w21-gold` at every rung while the rim, the
 * callout and the letterbox spill all climbed `ladderColour()` together. That
 * is a coherence bug rather than a taste call — the player is told by three
 * channels that a 5x is a different thing from a 2x, and by the one channel
 * they actually read the exact number off that it is not.
 *
 * Both functions below take the level (1..5) and go through the shared ladder,
 * so the pill can never disagree with the rim about what a run is worth.
 */

/** `0xFFC53D` → `'255 197 61'`, the triple form `rgb(... / a)` wants. */
function rgbTriple(hex: number): string {
  return `${(hex >> 16) & 0xff} ${(hex >> 8) & 0xff} ${hex & 0xff}`;
}

/**
 * Seconds per breath at a rung — **exactly four of the rim's heartbeats.**
 *
 * The aura's shader beats at `bpm = mix(1.15, 3.1, momentum)` cycles per
 * second. Picking the pill's period independently would give two things that
 * pulse at unrelated rates in the same eyeline, which reads as two effects
 * rather than one state. A whole-number ratio phase-locks them: the pill
 * breathes once per four rim beats, so every pill breath lands on a rim beat.
 *
 * Four rather than one because the rim is a wash of light and the pill is a
 * hairline around type — the same rate on both is a flicker on the pill.
 *
 * It also lands almost exactly where the old fixed value was:
 *
 * | rung | momentum | rim bpm | pill period |
 * |---|---|---|---|
 * | 2x | 0.30 | 1.74 | 2.31 s |
 * | 3x | 0.58 | 2.28 | 1.75 s |
 * | 4x | 0.80 | 2.71 | 1.48 s |
 * | 5x | 0.93 | 2.96 | 1.35 s |
 * | 6x | 1.00 | 3.10 | 1.29 s |
 *
 * 2.31 s at the bottom against the 2.4 s it replaces, so nothing about the
 * opening rung changes; the climb to 1.29 s is the escalation.
 */
export function streakBreathSeconds(level: number): number {
  return 4 / (1.15 + 1.95 * momentumAt(level));
}

/**
 * The rung-change bump: 6%, 140 ms, `power4.out`.
 *
 * Replaces a `back.out(2.4)` entrance from `scale: 0.7` and a 1.28 punch on a
 * rung change. Published premium practice caps a spring's first overshoot at a
 * few percent and says never to start an entrance below 0.9 — 0.7 with a
 * back-ease on top of it is cartoon motion, and it was the same mistake the
 * callout was making 44% of.
 *
 * `power4.out` puts 63% of the travel in the first fifth of the duration, so it
 * still reads as a snap; what it does not do is come back past 1.
 */
const PILL_BUMP = 1.06;
const PILL_BUMP_SECONDS = 0.14;
/** Opening entrance: a lift and a fade, no overshoot, no scale-from-nothing. */
const PILL_OPEN_SECONDS = 0.22;
const PILL_OPEN_SCALE = 0.94;

/** Seconds → `m:ss`. */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

let reducedMotionQuery: MediaQueryList | null = null;

/** Live check, not a snapshot — the OS setting can change while we run. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  reducedMotionQuery ??= window.matchMedia('(prefers-reduced-motion: reduce)');
  return reducedMotionQuery.matches;
}

/** Duration helper: collapses to an instant step when motion is reduced. */
export function dur(seconds: number): number {
  return prefersReducedMotion() ? 0 : seconds;
}

/**
 * Tween a number into a node. Returns the tween so callers can kill it, or
 * null when the value was written straight out (reduced motion / no change).
 */
export function countUp(
  node: HTMLElement,
  from: number,
  to: number,
  duration: number,
  format: (value: number) => string = formatScore,
): gsap.core.Tween | null {
  if (from === to || duration <= 0 || prefersReducedMotion()) {
    node.textContent = format(to);
    return null;
  }
  const proxy = { value: from };
  return gsap.to(proxy, {
    value: to,
    duration,
    ease: 'power2.out',
    onUpdate: () => {
      node.textContent = format(proxy.value);
    },
    onComplete: () => {
      node.textContent = format(to);
    },
  });
}

/** Seconds below which the clock changes character. */
export const URGENT_SECONDS = 15;

/**
 * Seconds at which the clock starts warming toward gold.
 *
 * The urgent state at 15s was a single binary threshold: from 3:00 to 0:16 the
 * clock did nothing at all except swap a digit once a second, then jumped
 * white to rose. This gives the last three quarters of a minute a continuous
 * ramp, so pressure builds instead of arriving.
 */
export const WARM_SECONDS = 45;

/* ----------------------------------------------------------------- hud -- */

export interface HudCallbacks {
  onUndo(): void;
  onPause(): void;
}

export interface HudOptions {
  callbacks: HudCallbacks;
  /** Round length, used for the timer track. Defaults to 180. */
  roundSeconds?: number;
  /** Cards in a full stock, used for the initial readout. Defaults to 52. */
  totalCards?: number;
}

export class Hud {
  readonly root: HTMLElement;
  readonly undoButton: HTMLButtonElement;
  readonly pauseButton: HTMLButtonElement;
  #bottom!: HTMLElement;
  #goFlash: HTMLElement | null = null;
  #reconnecting!: HTMLElement;
  #goTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

  /** Mutable: a round's length is the challenge's, so each `reset` may bring a new one. */
  #roundSeconds: number;
  #totalCards: number;
  readonly #scoreValue: HTMLElement;
  readonly #scoreDelta: HTMLElement;
  readonly #streak: HTMLElement;
  readonly #level: HTMLElement;
  /** Replaced on every roll — the outgoing glyph leaves with its own tween. */
  #levelN: HTMLElement;
  #levelShown = 1;
  /** Eased separately from the score so the bar never jumps a threshold. */
  #levelFill = 0;
  #levelTween: gsap.core.Tween | null = null;
  readonly #streakX: HTMLElement;
  readonly #streakStake: HTMLElement;
  readonly #score: HTMLElement;
  readonly #timer: HTMLElement;
  readonly #timerValue: HTMLElement;
  readonly #timerFill: HTMLElement;
  readonly #cardsValue: HTMLElement;
  readonly #pipRow: HTMLElement;
  readonly #pips: HTMLElement[] = [];

  #scoreShown = 0;
  #scoreTarget = 0;
  #scoreTween: gsap.core.Tween | null = null;
  #streakTween: gsap.core.Timeline | null = null;
  #deltaTween: gsap.core.Timeline | null = null;
  #streakLevel = 0;
  #busts = 0;
  #seconds: number;
  #canUndo = false;
  #enabled = true;
  #destroyed = false;

  constructor(options: HudOptions) {
    this.#roundSeconds = Math.max(1, options.roundSeconds ?? ROUND_SECONDS);
    this.#seconds = this.#roundSeconds;

    this.#totalCards = Math.max(0, options.totalCards ?? 52);

    this.root = el('div', 'w21-hud');
    this.root.dataset.visible = 'false';

    /* -- top row ---------------------------------------------------------- */
    const top = el('div', 'w21-hud__top');

    // Left: busts remaining, then cards left in the stock.
    const left = el('div', 'w21-hud-side w21-hud-side--start');

    const busts = el('div', 'w21-readout w21-readout--busts');
    this.#pipRow = el('div', 'w21-pips');
    this.#pipRow.setAttribute('role', 'img');
    for (let i = 0; i < MAX_BUSTS; i++) {
      const pip = el('span', 'w21-pip');
      pip.dataset.spent = 'false';
      this.#pips.push(pip);
      this.#pipRow.append(pip);
    }
    busts.append(this.#pipRow, el('div', 'w21-label', 'Busts left'));

    const cards = el('div', 'w21-readout');
    this.#cardsValue = el('div', 'w21-readout__value', String(this.#totalCards));
    cards.append(this.#cardsValue, el('div', 'w21-label', 'Cards'));

    left.append(busts, cards);

    // Centre: the hero number.
    const score = el('div', 'w21-hud-score');
    this.#score = score;
    this.#scoreDelta = el('div', 'w21-hud-score__delta');
    this.#scoreDelta.setAttribute('aria-hidden', 'true');
    // Deliberately not in a live region: a screen reader reading every frame of
    // a count-up is unusable. Resolutions are announced once, from index.ts.
    this.#scoreValue = el('div', 'w21-hud-score__value', '0');

    this.#streak = el('div', 'w21-streak');
    this.#streak.hidden = true;
    this.#streakX = el('span', 'w21-streak__x', STREAK_LABELS[0]);
    this.#streakStake = el('span', 'w21-streak__stake', '');
    this.#streak.append(this.#streakX, this.#streakStake);

    // Docked under the score rather than beside it: this HUD's centre column is
    // already three rows deep and the band is the width of the board, so a pill
    // parked to the right of a five-digit number is a pill that moves every time
    // the score gains a digit.
    this.#level = el('div', 'w21-hudlv');
    this.#levelN = el('span', 'w21-hudlv__n', '1');
    this.#level.append(el('span', 'w21-hudlv__label', 'LV'), this.#levelN);
    this.#level.setAttribute('aria-hidden', 'true');

    score.append(
      this.#scoreDelta,
      this.#scoreValue,
      el('div', 'w21-label', 'Score'),
      this.#level,
      this.#streak,
    );

    // Right: the clock.
    const right = el('div', 'w21-hud-side w21-hud-side--end');
    this.#timer = el('div', 'w21-readout w21-readout--end w21-timer');
    this.#timer.dataset.urgent = 'false';
    this.#timerValue = el('div', 'w21-timer__value', formatClock(this.#seconds));
    const track = el('div', 'w21-timer__track');
    this.#timerFill = el('div', 'w21-timer__fill');
    track.append(this.#timerFill);
    this.#timer.append(this.#timerValue, el('div', 'w21-label', 'Time'), track);
    right.append(this.#timer);

    top.append(left, score, right);

    /* -- bottom row ------------------------------------------------------- */
    const bottom = el('div', 'w21-hud__bottom');

    // The kit's small button: 40 / 44 on a coarse pointer, r10, 14/700.
    this.undoButton = el('button', 'w21-btn w21-sm');
    this.undoButton.type = 'button';
    this.undoButton.disabled = true;
    this.undoButton.setAttribute('aria-label', 'Undo the last play');
    const undoKey = el('kbd', 'w21-key', 'U');
    undoKey.setAttribute('aria-hidden', 'true');
    this.undoButton.append(icon('undo'), el('span', undefined, 'Undo'), undoKey);
    this.undoButton.addEventListener('click', () => options.callbacks.onUndo());

    this.pauseButton = el('button', 'w21-btn w21-ghost');
    this.pauseButton.type = 'button';
    this.pauseButton.setAttribute('aria-label', 'Pause the round');
    const pauseKey = el('kbd', 'w21-key', 'Space');
    pauseKey.setAttribute('aria-hidden', 'true');
    this.pauseButton.append(icon('pause'), el('span', undefined, 'Pause'), pauseKey);
    this.pauseButton.addEventListener('click', () => options.callbacks.onPause());

    // The ⚙ in the game chrome opens the sheet now; the old Pause button
    // stays wired for the keyboard hint and the tests, out of the layout.
    this.pauseButton.hidden = true;
    bottom.append(this.undoButton, this.pauseButton);
    this.#bottom = bottom;

    this.root.append(top, bottom);

    // Always in the page, empty until it has something to say: a polite live region announces a change in
    // text it already holds, where one inserted with its text is missed by some screen readers.
    this.#reconnecting = el('div', 'w21-reconnecting');
    this.#reconnecting.setAttribute('role', 'status');
    this.#reconnecting.setAttribute('aria-live', 'polite');
    this.#reconnecting.dataset.on = 'false';
    this.#reconnecting.append(el('span'));
    this.root.append(this.#reconnecting);

    this.#applyPips(true);
    this.#applyTimer(true);
    this.#applyUndoState();
  }

  /**
   * Seat the round's chrome (✕ and ⚙) in the bottom row's free corner, after
   * Undo. Adam's call: at the top they sat on the bust dots and the clock,
   * and giving them their own strip shrank the table. Down here they share
   * the row the felt already keeps clear, so nothing else moves.
   */
  dock(chrome: HTMLElement): void {
    chrome.classList.add('w21-game-chrome--docked');
    this.#bottom.append(chrome);
  }

  /**
   * The 'GO' at the moment the clock starts: a small tracked word between two
   * hairlines, hung under the score cluster so it covers no card. Never takes
   * a tap, adds nothing to the layout, and removes itself after
   * `GO_FLASH_MS` (a second call replaces the one in flight). It is a polite
   * status, so a screen reader hears it once.
   */
  flashGo(): void {
    this.#clearGo();
    const flash = el('div', 'w21-go-flash');
    flash.setAttribute('role', 'status');
    flash.setAttribute('aria-live', 'polite');
    flash.append(el('i', 'w21-go-flash__rule'), el('span', 'w21-go-flash__word', 'Go'), el('i', 'w21-go-flash__rule'));
    this.#score.append(flash);
    this.#timer.dataset.go = 'true';
    this.#goFlash = flash;
    this.#goTimer = globalThis.setTimeout(() => this.#clearGo(), GO_FLASH_MS);
  }

  /**
   * 'Reconnecting…', centred over the table, while the round is waiting on an answer the connection has not
   * brought. A status, not a dialog: it takes no tap, holds no focus, sits in the HUD's own box (so nothing
   * shifts) and the clock behind it does not stop. Shown and hidden by the round, the moment play can or
   * cannot continue.
   */
  setReconnecting(on: boolean): void {
    if (this.#destroyed) return;
    this.#reconnecting.dataset.on = on ? 'true' : 'false';
    const label = this.#reconnecting.firstElementChild;
    if (label) label.textContent = on ? 'Reconnecting…' : '';
  }

  #clearGo(): void {
    if (this.#goTimer !== null) globalThis.clearTimeout(this.#goTimer);
    this.#goTimer = null;
    this.#goFlash?.remove();
    this.#goFlash = null;
    delete this.#timer.dataset.go;
  }

  /* ---------------------------------------------------------- visibility -- */

  setVisible(visible: boolean): void {
    this.root.dataset.visible = visible ? 'true' : 'false';
  }

  /**
   * Turn the HUD's own controls off while a modal screen is up, so tab focus
   * cannot wander behind the scrim.
   */
  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    this.pauseButton.disabled = !enabled;
    this.#applyUndoState();
  }

  /* --------------------------------------------------------------- state -- */

  /** Fold a whole snapshot in. Safe to call on every event. */
  sync(snapshot: GameSnapshot): void {
    this.setScore(snapshot.score);
    this.setLevelProgress(snapshot.score);
    this.levelUp(snapshot.level);
    this.setTime(snapshot.secondsRemaining);
    this.setCards(snapshot.stock.length);
    this.setBusts(snapshot.busts);
    this.setStreak(snapshot.streakLevel);
    this.setCanUndo(snapshot.canUndo);
  }

  /**
   * Back to an unplayed round without animating anything.
   *
   * The opening readouts are the new round's own: pass its length and stock
   * size and the timer track and the card count start from those (a round
   * picked up mid-way passes its full length and deck, not what is left).
   * Omit either to keep what the HUD had.
   */
  reset(round: { roundSeconds?: number; totalCards?: number } = {}): void {
    if (round.roundSeconds !== undefined) this.#roundSeconds = Math.max(1, round.roundSeconds);
    if (round.totalCards !== undefined) this.#totalCards = Math.max(0, round.totalCards);
    this.#killTweens();
    this.#clearGo();
    this.#scoreShown = 0;
    this.#scoreTarget = 0;
    this.#scoreValue.textContent = '0';
    this.#levelTween?.kill();
    this.#levelTween = null;
    this.#levelShown = 1;
    this.#levelFill = 0;
    this.#levelN.textContent = '1';
    this.#level.style.setProperty('--w21-hudlv-fill', '6%');
    delete this.#level.dataset.pop;
    gsap.set(this.#scoreValue, { clearProps: 'transform,opacity' });
    gsap.set(this.#scoreDelta, { opacity: 0 });
    this.#streakLevel = 0;
    this.#streak.hidden = true;
    delete this.#streak.dataset.broken;
    delete this.#streak.dataset.kick;
    gsap.set(this.#streak, { clearProps: 'transform,opacity' });
    this.#busts = 0;
    this.#applyPips(true);
    this.#seconds = this.#roundSeconds;
    this.#applyTimer(true);
    this.#cardsValue.textContent = String(this.#totalCards);
    this.#canUndo = false;
    this.#applyUndoState();
  }

  /**
   * Move the level pill's fill to match a score.
   *
   * Eased rather than set, and eased on its own clock rather than riding the
   * score count-up: the fill is a fraction of the *current* level, so at the
   * moment a threshold is crossed the underlying value drops from ~1 back to 0.
   * Driving the bar straight from the score would play that as a bar racing to
   * full and then snapping empty. Tweening the width instead lets it fill,
   * reset, and fill again as three readable movements.
   */
  setLevelProgress(score: number, immediate = false): void {
    const target = levelProgress(score);
    // Never below a sliver: a player who has just levelled up is at exactly 0,
    // and a pill with no fill at all reads as a pill with no feature.
    const width = Math.max(0.06, target);
    if (immediate || prefersReducedMotion()) {
      this.#levelTween?.kill();
      this.#levelTween = null;
      this.#levelFill = width;
      this.#level.style.setProperty('--w21-hudlv-fill', `${(width * 100).toFixed(1)}%`);
      return;
    }

    this.#levelTween?.kill();
    const proxy = { value: this.#levelFill };
    this.#levelTween = gsap.to(proxy, {
      value: width,
      duration: 0.45,
      ease: 'power2.out',
      onUpdate: () => {
        this.#levelFill = proxy.value;
        this.#level.style.setProperty('--w21-hudlv-fill', `${(proxy.value * 100).toFixed(1)}%`);
      },
    });
  }

  /**
   * The number rolled over.
   *
   * The outgoing glyph holds at full alpha before it leaves, so no frame of the
   * flip shows an empty slot or two digits at once — the two ways a split-flap
   * reads as a glitch rather than a mechanism.
   *
   * Quiet while a streak pill is up. Two pills competing for the same glance is
   * the one thing this HUD cannot afford, and the streak is the one carrying
   * points.
   */
  levelUp(level: number): void {
    if (level === this.#levelShown) return;
    this.#levelShown = level;

    if (prefersReducedMotion()) {
      this.#levelN.textContent = String(level);
      return;
    }

    const outgoing = this.#levelN;
    const incoming = el('span', 'w21-hudlv__n', String(level));
    outgoing.after(incoming);
    this.#levelN = incoming;

    gsap.timeline()
      .set(incoming, { yPercent: 70, opacity: 0 })
      .to(outgoing, { yPercent: -70, opacity: 0, duration: 0.16, ease: 'power2.in', delay: 0.04 })
      .to(incoming, { yPercent: 0, opacity: 1, duration: 0.2, ease: 'power3.out' }, '-=0.1')
      .call(() => outgoing.remove());

    // The pill itself acknowledges it, unless a streak is already shouting.
    if (this.#streak.hidden) {
      this.#level.dataset.pop = 'true';
      window.setTimeout(() => delete this.#level.dataset.pop, 560);
    }
  }

  /**
   * The score always travels — it counts up from whatever is on screen to the
   * new total, so a bonus reads as an event rather than a number swap.
   */
  setScore(score: number, immediate = false): void {
    if (score === this.#scoreTarget) return;
    const rising = score > this.#scoreTarget;
    this.#scoreTarget = score;
    this.#scoreTween?.kill();
    this.#scoreTween = null;

    if (immediate || prefersReducedMotion()) {
      this.#scoreShown = score;
      this.#scoreValue.textContent = formatScore(score);
      return;
    }

    const delta = Math.abs(score - this.#scoreShown);
    const duration = Math.min(0.95, 0.3 + delta / 2600);
    const proxy = { value: this.#scoreShown };
    this.#scoreTween = gsap.to(proxy, {
      value: score,
      duration,
      ease: 'power2.out',
      onUpdate: () => {
        this.#scoreShown = proxy.value;
        this.#scoreValue.textContent = formatScore(proxy.value);
      },
      onComplete: () => {
        this.#scoreShown = score;
        this.#scoreValue.textContent = formatScore(score);
        this.#scoreTween = null;
      },
    });

    if (rising) {
      gsap.fromTo(
        this.#scoreValue,
        { scale: 1.09 },
        { scale: 1, duration: motion.settle.duration, ease: motion.settle.ease, overwrite: true },
      );
      // Push real light out of the number, not just scale it. `--w21-hud-score-heat`
      // drives the glow radius and alpha in the CSS filter, so a gain reads as
      // the total catching fire rather than as a tick.
      gsap
        .timeline({ overwrite: true })
        .set(this.#scoreValue, { '--w21-hud-score-heat': 1 })
        .to(this.#scoreValue, {
          '--w21-hud-score-heat': 0,
          duration: 0.55,
          ease: 'power2.out',
        });
    } else {
      // Undo rolls the score backwards, and previously did so in complete
      // silence: the pop is skipped when falling, `flashDelta` is not called
      // for an undo at all, and the delta chip is hardcoded gold so a minus
      // would have read as a gain. A contraction is the inverse of the gain
      // pop and costs nothing.
      gsap.fromTo(
        this.#scoreValue,
        { scale: 0.94 },
        { scale: 1, duration: motion.settle.duration, ease: motion.settle.ease, overwrite: true },
      );
    }
  }

  /** The little `+400` that rises off the score. */
  /**
   * The gain chip, rising out of the score — or falling below it.
   *
   * It rises everywhere it can, because a number that goes up is the whole
   * point of it. The exception is a landscape phone in a browser, where up is
   * where the toolbar is: the HUD is already pushed clear of that band, and a
   * chip travelling 44px back into it was reported as the score being visible
   * while the `+` above it was not. Down has the felt under it and cannot run
   * out of room, so the direction flips rather than the distance shrinking.
   *
   * `--w21-chrome-t` is the signal, written by the renderer, so this and the
   * felt agree about whether there is a toolbar without either deciding for
   * itself what counts as a phone.
   */
  flashDelta(delta: number): void {
    if (delta === 0 || prefersReducedMotion()) return;
    this.#deltaTween?.kill();
    const sign = delta > 0 ? '+' : '−';
    this.#scoreDelta.textContent = `${sign}${formatScore(Math.abs(delta))}`;

    const below = this.#gainFallsBelow();
    this.#scoreDelta.classList.toggle('w21-hud-score__delta--below', below);
    const dir = below ? -1 : 1;

    this.#deltaTween = gsap
      .timeline()
      .fromTo(
        this.#scoreDelta,
        { xPercent: -50, y: 6 * dir, scale: 0.9, opacity: 0 },
        { xPercent: -50, y: -22 * dir, scale: 1, opacity: 1, duration: 0.28, ease: 'power2.out' },
      )
      .to(
        this.#scoreDelta,
        { y: -44 * dir, opacity: 0, duration: 0.46, ease: motion.exit },
        '+=0.32',
      );
  }

  /** True when the gain has no room above it — see {@link flashDelta}. */
  #gainFallsBelow(): boolean {
    const root = globalThis.document?.documentElement;
    if (!root) return false;
    return root.classList.contains('w21-gain-below');
  }

  setCards(remaining: number): void {
    const text = String(Math.max(0, remaining));
    if (this.#cardsValue.textContent !== text) this.#cardsValue.textContent = text;
  }

  setTime(secondsRemaining: number): void {
    const next = Math.max(0, Math.round(secondsRemaining));
    if (next === this.#seconds) return;
    const wasUrgent = this.#seconds <= URGENT_SECONDS;
    this.#seconds = next;
    this.#applyTimer(false);

    // In the final fifteen the clock gets a heartbeat, one beat per second.
    if (next > 0 && next <= URGENT_SECONDS && !prefersReducedMotion()) {
      gsap.fromTo(
        this.#timerValue,
        { scale: wasUrgent ? 1.1 : 1.2 },
        { scale: 1, duration: 0.44, ease: 'power2.out', overwrite: true },
      );
    }
  }

  setBusts(busts: number): void {
    const next = Math.max(0, Math.min(MAX_BUSTS, Math.round(busts)));
    if (next === this.#busts) return;
    const spending = next > this.#busts;
    const first = this.#busts;
    this.#busts = next;

    if (!spending || prefersReducedMotion()) {
      this.#applyPips(false);
      for (let i = 0; i < this.#pips.length; i++) {
        const pip = this.#pips[i];
        if (pip) gsap.set(pip, { scale: i < next ? PIP_SPENT_SCALE : 1 });
      }
      return;
    }

    // Losing a life is animated as a loss, not as its aftermath.
    //
    // Previously `#applyPips` flipped the pip to spent *before* the tween
    // started, so what you actually saw was a large, already-dead rose ring
    // shrinking — there was no frame in which the live pip was seen dying. The
    // colour swap now happens at the peak of the flare, so the sequence reads
    // flare, die, settle.
    this.#applyPips(false, first);

    for (let i = first; i < next; i++) {
      const pip = this.#pips[i];
      if (!pip) continue;
      gsap
        .timeline({ overwrite: true })
        .to(pip, { scale: 2.6, duration: 0.1, ease: 'power2.out' })
        .add(() => {
          pip.dataset.spent = 'true';
        })
        .to(pip, { scale: 0.7, duration: 0.34, ease: 'back.in(2)' })
        .to(pip, { scale: PIP_SPENT_SCALE, duration: 0.42, ease: 'elastic.out(1, 0.55)' });
    }
  }

  /** Streak level 0 (none) or 1..5, rendered as the 2x..6x callouts. */
  setStreak(level: number): void {
    const next = Math.max(0, Math.round(level));
    if (next === this.#streakLevel) return;
    const previous = this.#streakLevel;
    this.#streakLevel = next;
    this.#streakTween?.kill();
    this.#streakTween = null;

    if (next === 0) {
      if (previous > 0) this.#breakStreak();
      return;
    }

    const label = STREAK_LABELS[Math.min(next, STREAK_LABELS.length) - 1] ?? '';
    this.#streakX.textContent = label;
    this.#streakStake.textContent = `${formatScore(streakBonusOf(next))} at stake`;
    delete this.#streak.dataset.broken;
    this.#streak.hidden = false;
    this.#paintStreak(next);

    if (prefersReducedMotion()) {
      gsap.set(this.#streak, { clearProps: 'transform,opacity' });
      return;
    }

    if (previous === 0) {
      // A lift and a fade. Nothing scales up from nothing and nothing comes
      // back past its final size; see PILL_BUMP.
      this.#streakTween = gsap
        .timeline()
        .fromTo(
          this.#streak,
          { y: 6, scale: PILL_OPEN_SCALE, opacity: 0 },
          { y: 0, scale: 1, opacity: 1, duration: PILL_OPEN_SECONDS, ease: 'power4.out' },
        );
    } else {
      // A rung change is a hit on something already on screen, so it is a bump
      // rather than an entrance — and the flash is what carries the new colour
      // in, so the pill is *seen* to change rather than found to have changed.
      this.#kickStreak();
      this.#streakTween = gsap
        .timeline()
        .fromTo(
          this.#streak,
          { scale: PILL_BUMP },
          { scale: 1, duration: PILL_BUMP_SECONDS, ease: 'power4.out' },
        );
    }
  }

  /**
   * Put the pill in the rung's colour and at the rung's breathing rate.
   *
   * Two custom properties rather than a class per rung: the ladder's colours
   * are computed (each is lifted toward a target luminance at module load, see
   * `@/vfx/streakLadder`), so they cannot be written down in the stylesheet
   * without going stale the moment the ladder is retuned.
   */
  #paintStreak(level: number): void {
    const style = this.#streak.style;
    style.setProperty('--w21-streak-rgb', rgbTriple(ladderColourAt(level)));
    style.setProperty('--w21-streak-breath', `${streakBreathSeconds(level).toFixed(3)}s`);
  }

  /**
   * One flash to white as a rung lands.
   *
   * A CSS animation rather than a tween, because the pill's resting glow is
   * itself a CSS animation on `box-shadow` and a GSAP tween of the same
   * property would fight it for the duration and then hand back a stale inline
   * value. Restarting a one-shot animation needs the attribute to be removed,
   * the style flushed, and the attribute put back — `offsetWidth` is that
   * flush.
   */
  #kickStreak(): void {
    if (prefersReducedMotion()) return;
    delete this.#streak.dataset.kick;
    void this.#streak.offsetWidth;
    this.#streak.dataset.kick = 'true';
  }

  setCanUndo(canUndo: boolean): void {
    if (canUndo === this.#canUndo) return;
    this.#canUndo = canUndo;
    this.#applyUndoState();
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#killTweens();
    this.#clearGo();
    this.root.remove();
  }

  /* ------------------------------------------------------------ internal -- */

  #killTweens(): void {
    this.#scoreTween?.kill();
    this.#scoreTween = null;
    this.#streakTween?.kill();
    this.#streakTween = null;
    this.#deltaTween?.kill();
    this.#deltaTween = null;
    gsap.killTweensOf([this.#scoreValue, this.#scoreDelta, this.#streak, this.#timerValue]);
    gsap.killTweensOf(this.#pips);
  }

  /**
   * Losing a streak has to cost something visible, or the player never learns
   * that it was worth protecting: the chip turns rose, snaps, and drops away.
   */
  #breakStreak(): void {
    delete this.#streak.dataset.kick;
    if (prefersReducedMotion()) {
      this.#streak.hidden = true;
      delete this.#streak.dataset.broken;
      return;
    }
    this.#streak.dataset.broken = 'true';
    this.#streakTween = gsap
      .timeline({
        onComplete: () => {
          this.#streak.hidden = true;
          delete this.#streak.dataset.broken;
          gsap.set(this.#streak, { clearProps: 'transform,opacity' });
          this.#streakTween = null;
        },
      })
      .to(this.#streak, { scale: 1.18, duration: 0.09, ease: 'power2.out' })
      .to(this.#streak, {
        scale: 0.84,
        y: 16,
        rotation: -3,
        opacity: 0,
        duration: 0.44,
        ease: motion.exit,
      });
  }

  /**
   * @param instant - clear any GSAP transform rather than leaving it in place.
   * @param spentCount - how many pips to *mark* spent. Defaults to the true
   *   bust count; `setBusts` passes the previous count so the pip being lost
   *   can flare while still lit, and flips itself at the peak of the flare.
   */
  #applyPips(instant: boolean, spentCount = this.#busts): void {
    for (let i = 0; i < this.#pips.length; i++) {
      const pip = this.#pips[i];
      if (!pip) continue;
      const spent = i < spentCount;
      pip.dataset.spent = spent ? 'true' : 'false';
      // The one pip still lit when only a single bust remains gets a heartbeat.
      // Derived from the true count, not `spentCount`: the heartbeat marks the
      // genuinely last life, which is correct even mid-flare.
      const last = i >= spentCount && this.#busts === MAX_BUSTS - 1 && i === this.#busts;
      if (last) pip.dataset.last = 'true';
      else delete pip.dataset.last;
      if (instant) gsap.set(pip, { clearProps: 'transform' });
    }
    const left = MAX_BUSTS - this.#busts;
    this.#pipRow.setAttribute('aria-label', `${left} of ${MAX_BUSTS} busts left`);
  }

  #applyTimer(instant: boolean): void {
    this.#timerValue.textContent = formatClock(this.#seconds);
    const fraction = Math.max(0, Math.min(1, this.#seconds / this.#roundSeconds));
    // A custom property driving `transform: scaleX()`, not `width` — see the
    // note on `.w21-timer__fill`.
    if (instant) {
      const previous = this.#timerFill.style.transition;
      this.#timerFill.style.transition = 'none';
      this.#timerFill.style.setProperty('--w21-time-fraction', fraction.toFixed(4));
      void this.#timerFill.offsetWidth;
      this.#timerFill.style.transition = previous;
    } else {
      this.#timerFill.style.setProperty('--w21-time-fraction', fraction.toFixed(4));
    }

    const urgent = this.#seconds <= URGENT_SECONDS;
    this.#timer.dataset.urgent = urgent ? 'true' : 'false';
    const urgency = urgent ? 1 - Math.max(0, this.#seconds) / URGENT_SECONDS : 0;
    this.#timer.style.setProperty('--w21-urgency', urgency.toFixed(3));

    // A continuous warmth ramp, so the clock is not inert for 165 of its 180
    // seconds and then snapped.
    //
    // `--w21-urgency` only exists inside the final fifteen; this runs the whole
    // round. 0 above WARM_SECONDS, ramping to 1 at URGENT_SECONDS, and pinned
    // at 1 below that so the rose state owns the endgame.
    const heat =
      this.#seconds >= WARM_SECONDS
        ? 0
        : Math.min(
            1,
            (WARM_SECONDS - Math.max(this.#seconds, 0)) / (WARM_SECONDS - URGENT_SECONDS),
          );
    this.#timer.style.setProperty('--w21-time-heat', heat.toFixed(3));
  }

  #applyUndoState(): void {
    this.undoButton.disabled = !this.#canUndo || !this.#enabled;
    this.undoButton.setAttribute('aria-disabled', this.undoButton.disabled ? 'true' : 'false');
  }
}
